import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import { ReportChartRow, BarCard, asSeries, sumField } from './ReportCharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const toTitleCase = v => (v || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const today     = new Date().toISOString().split('T')[0];
const thirtyAgo = subDays(new Date(), 30).toISOString().split('T')[0];

const COLS = [
  {
    key: 'shift_type',
    label: 'Service Type',
    render: v => <span className="font-medium capitalize">{toTitleCase(v)}</span>,
  },
  { key: 'shift_count',      label: 'Shifts',          render: v => v || 0 },
  {
    key: 'completed_count',
    label: 'Completed',
    render: v => <span className="text-green-700 font-semibold">{v || 0}</span>,
  },
  {
    key: 'cancelled_count',
    label: 'Cancelled',
    render: v => <span className={parseInt(v) > 0 ? 'text-red-600 font-semibold' : 'text-slate-500'}>{v || 0}</span>,
  },
  { key: 'unique_staff',     label: 'Unique Staff',    render: v => v || 0 },
  { key: 'unique_clients',   label: 'Unique Clients',  render: v => v || 0 },
  {
    key: 'total_hours',
    label: 'Total Hours',
    render: v => v ? `${parseFloat(v).toFixed(1)} hrs` : '–',
  },
];

export default function ServiceTypeReport() {
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]         = useState(thirtyAgo);
  const [endDate, setEnd]             = useState(today);
  const [pendingStart, setPendingStart] = useState(thirtyAgo);
  const [pendingEnd, setPendingEnd]     = useState(today);

  const pagination = usePagination(data, 100);

  const load = useCallback((sd, ed) => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: sd, end_date: ed });
    api.get(`/reports/service-type?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(thirtyAgo, today); }, [load]);

  const handleApply = () => {
    setStart(pendingStart);
    setEnd(pendingEnd);
    load(pendingStart, pendingEnd);
  };

  const summary = useMemo(() => ({
    types:      data.length,
    totalShifts: data.reduce((s, r) => s + (parseInt(r.shift_count) || 0), 0),
    totalHours:  data.reduce((s, r) => s + (parseFloat(r.total_hours) || 0), 0),
  }), [data]);

  const chips = [{ label: 'Date Range', value: `${startDate} — ${endDate}` }];

  return (
    <ReportLayout
      title="Service Type Report"
      data={data}
      filename="service_type_report"
      columns={COLS}
      activeChips={chips}
      showFiltersPanel={showFilters}
      onToggleFilters={() => setShowFilters(v => !v)}
      chart={
        <ReportChartRow>
          <BarCard title="Shifts by service type" data={asSeries(data, 'shift_type', 'shift_count', { label: toTitleCase })} />
          <BarCard title="Completed vs cancelled (total)" data={[{ name: 'Completed', value: sumField(data, 'completed_count') }, { name: 'Cancelled', value: sumField(data, 'cancelled_count') }]} color="#10B981" />
        </ReportChartRow>
      }
      filters={
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={pendingStart} onChange={e => setPendingStart(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={pendingEnd} onChange={e => setPendingEnd(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={handleApply} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-3 gap-4 mb-4">
        {[
          { label: 'Service Types',  value: summary.types,                                                    color: 'text-slate-900' },
          { label: 'Total Shifts',   value: summary.totalShifts,                                              color: 'text-blue-600'  },
          { label: 'Total Hours',    value: `${summary.totalHours.toFixed(1)} hrs`,                           color: 'text-green-600' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 mb-1">{card.label}</div>
            <div className={clsx('text-2xl font-bold', card.color)}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Horizontal bar chart — top 5 service types by shift count */}
      {data.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Top Service Types by Shifts</h3>
          <div className="space-y-2">
            {data.slice(0, 5).map(r => {
              const maxShifts = Math.max(...data.map(x => parseInt(x.shift_count) || 0), 1);
              const pct = Math.round(((parseInt(r.shift_count) || 0) / maxShifts) * 100);
              return (
                <div key={r.shift_type} className="flex items-center gap-3">
                  <div className="w-40 text-xs text-right text-slate-600 shrink-0 capitalize">{(r.shift_type || '').replace(/_/g, ' ')}</div>
                  <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-12 text-xs text-slate-500 shrink-0">{r.shift_count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ReportTable
        columns={COLS}
        data={data}
        loading={loading}
        pagination={pagination}
        emptyMsg="No shift data found for this period."
      />
    </ReportLayout>
  );
}
